/**
 * GroupActivityPage - Attività di Gruppo
 * Il Diario di Bordo - SaaS
 *
 * Flusso a 3 step:
 * 1. Seleziona valori da entrambe le sezioni (Attività di Gruppo + Tempo Libero)
 * 2. Seleziona partecipanti (griglia)
 * 3. Riepilogo → Conferma
 *
 * Se l'entry esiste: aggiunge valori. Se non esiste: salva in PendingActivity.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { minoriApi, entriesApi, settingsApi } from '../services/api'
import {
  UsersRound, ArrowLeft, ArrowRight, Check, Loader2
} from 'lucide-react'

const extractMinori = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.minori)) return data.minori
  if (Array.isArray(data?.data?.minori)) return data.data.minori
  return []
}

export default function GroupActivityPage() {
  const { tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Dati
  const [minori, setMinori] = useState([])
  const [fieldOptions, setFieldOptions] = useState({})

  // Step 1: Valori selezionati per entrambe le sezioni
  const [valoriGruppo, setValoriGruppo] = useState([])
  const [valoriTempoLibero, setValoriTempoLibero] = useState([])

  // Step 2: Partecipanti
  const [partecipanti, setPartecipanti] = useState(new Set())

  // Descrizione (solo per tempoLibero con "propone attività" o "altro")
  const [descrizione, setDescrizione] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [minoriRes, settingsRes] = await Promise.all([
        minoriApi.list({ attivo: true }),
        settingsApi.get()
      ])
      setMinori(extractMinori(minoriRes))
      const settings = settingsRes.data?.data || settingsRes.data
      setFieldOptions(settings?.fieldOptions || {})
    } catch (error) {
      showError('Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  // Normalizza opzioni in { value, label } come fa NewEntryPage
  const normalizeOption = (opt) => {
    if (typeof opt === 'string') {
      return { value: opt.toLowerCase().replace(/\s+/g, '_'), label: opt }
    }
    return opt
  }

  const opzioniGruppo = (fieldOptions.attivitaGruppo || []).map(normalizeOption)
  const opzioniTempoLibero = (fieldOptions.tempoLibero || []).map(normalizeOption)

  const toggleValoreGruppo = (val) => {
    setValoriGruppo(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    )
  }

  const toggleValoreTempoLibero = (val) => {
    setValoriTempoLibero(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    )
  }

  // Mostra textarea solo per tempoLibero con "propone attività" o "altro"
  const shouldShowDescrizione = () => {
    return valoriTempoLibero.some(v => {
      const val = v.toLowerCase()
      return val.includes('propone') || val.includes('altro')
    })
  }

  // Almeno 1 chip selezionato da qualsiasi sezione
  const hasSelection = valoriGruppo.length > 0 || valoriTempoLibero.length > 0

  const togglePartecipante = (id) => {
    setPartecipanti(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (partecipanti.size === minori.length) {
      setPartecipanti(new Set())
    } else {
      setPartecipanti(new Set(minori.map(m => m._id)))
    }
  }

  const handleConfirm = async () => {
    if (partecipanti.size === 0 || !hasSelection) return

    setSaving(true)
    try {
      const minoreIds = Array.from(partecipanti)
      const calls = []

      if (valoriGruppo.length > 0) {
        calls.push(entriesApi.groupActivity({
          minoreIds,
          tipoAttivita: 'attivitaGruppo',
          valori: valoriGruppo
        }))
      }

      if (valoriTempoLibero.length > 0) {
        calls.push(entriesApi.groupActivity({
          minoreIds,
          tipoAttivita: 'tempoLibero',
          valori: valoriTempoLibero,
          descrizione: shouldShowDescrizione() ? (descrizione || undefined) : undefined
        }))
      }

      const results = await Promise.all(calls)

      // Somma updated e pending da tutte le risposte
      const totals = results.reduce((acc, res) => {
        const data = res.data?.data || res.data
        return {
          updated: acc.updated + (data.updated || 0),
          pending: acc.pending + (data.pending || 0)
        }
      }, { updated: 0, pending: 0 })

      showSuccess(`Attività salvata: ${totals.updated} aggiornate, ${totals.pending} in attesa`)
      navigate('/dashboard')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  // Helper: trova label da value per il riepilogo
  const findLabel = (options, value) => {
    const opt = options.find(o => o.value === value)
    return opt?.label || value
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/dashboard')}
          className="mr-3 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <UsersRound className="h-6 w-6 mr-2 text-indigo-500" />
            Attività di Gruppo
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Step {step} di 3
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex gap-2">
        {[1, 2, 3].map(s => (
          <div
            key={s}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              s <= step ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Entrambe le sezioni */}
      {step === 1 && (
        <div className="space-y-4">

          {/* Card Attività di Gruppo */}
          <div className="rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 space-y-3">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-xl">👥</span> Attività di Gruppo
              {valoriGruppo.length > 0 && (
                <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-2 py-0.5 rounded-full">
                  {valoriGruppo.length} sel.
                </span>
              )}
            </h2>
            <div className="flex flex-col gap-2">
              {opzioniGruppo.map(opt => {
                const selected = valoriGruppo.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleValoreGruppo(opt.value)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
                      selected
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 ring-2 ring-indigo-400'
                        : 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {selected && <Check className="h-4 w-4 mr-2 flex-shrink-0" />}
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Card Tempo Libero */}
          <div className="rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 space-y-3">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="text-xl">🎮</span> Tempo Libero
              {valoriTempoLibero.length > 0 && (
                <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-2 py-0.5 rounded-full">
                  {valoriTempoLibero.length} sel.
                </span>
              )}
            </h2>
            <div className="flex flex-col gap-2">
              {opzioniTempoLibero.map(opt => {
                const selected = valoriTempoLibero.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleValoreTempoLibero(opt.value)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center ${
                      selected
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 ring-2 ring-indigo-400'
                        : 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {selected && <Check className="h-4 w-4 mr-2 flex-shrink-0" />}
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {/* Textarea condizionale */}
            {shouldShowDescrizione() && (
              <div className="pt-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrizione attività
                </label>
                <textarea
                  value={descrizione}
                  onChange={e => setDescrizione(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Descrivi le attività proposte o altro..."
                  className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() => setStep(2)}
              disabled={!hasSelection}
              className="btn-primary flex items-center gap-2"
            >
              Avanti <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Partecipanti */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">Seleziona partecipanti</h2>
            <button
              onClick={selectAll}
              className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
            >
              {partecipanti.size === minori.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
            </button>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {partecipanti.size} selezionati su {minori.length}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {minori.map(minore => {
              const selected = partecipanti.has(minore._id)
              return (
                <div
                  key={minore._id}
                  onClick={() => togglePartecipante(minore._id)}
                  className={`p-3 rounded-xl border-2 cursor-pointer select-none transition-all active:scale-95 ${
                    selected
                      ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-600'
                      : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-1 ${
                    selected
                      ? 'bg-indigo-200 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {selected ? <Check className="h-5 w-5" /> : (() => {
                      const parts = (minore.nomeCompleto || '').trim().split(/\s+/)
                      return parts.length >= 2
                        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
                        : (minore.nomeCompleto?.[0] || '?').toUpperCase()
                    })()}
                  </div>
                  <p className="text-xs font-medium text-gray-900 dark:text-white text-center truncate">
                    {minore.nomeCompleto || 'Senza nome'}
                  </p>
                </div>
              )
            })}
          </div>

          <div className="flex justify-between pt-4">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Indietro
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={partecipanti.size === 0}
              className="btn-primary flex items-center gap-2"
            >
              Avanti <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Riepilogo e Conferma */}
      {step === 3 && (
        <div className="space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">Riepilogo</h2>

          <div className="card p-4 space-y-3">
            {/* Attività di Gruppo */}
            {valoriGruppo.length > 0 && (
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <span>👥</span> Attività di Gruppo
                </span>
                <div className="flex flex-wrap gap-1 pl-6">
                  {valoriGruppo.map(v => (
                    <span key={v} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded text-xs">
                      {findLabel(opzioniGruppo, v)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tempo Libero */}
            {valoriTempoLibero.length > 0 && (
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <span>🎮</span> Tempo Libero
                </span>
                <div className="flex flex-wrap gap-1 pl-6">
                  {valoriTempoLibero.map(v => (
                    <span key={v} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded text-xs">
                      {findLabel(opzioniTempoLibero, v)}
                    </span>
                  ))}
                </div>
                {descrizione && (
                  <div className="flex items-start text-sm pl-6 pt-1">
                    <span className="text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0">Descrizione:</span>
                    <span className="text-gray-900 dark:text-white">{descrizione}</span>
                  </div>
                )}
              </div>
            )}

            {/* Partecipanti */}
            <div className="flex items-center text-sm pt-1 border-t border-gray-100 dark:border-gray-700">
              <span className="text-gray-500 dark:text-gray-400 w-24">Partecipanti:</span>
              <span className="font-medium text-gray-900 dark:text-white">{partecipanti.size} minori</span>
            </div>
          </div>

          {/* Bottoni */}
          <div className="flex justify-between pt-4">
            <button onClick={() => setStep(2)} className="btn-secondary">
              Indietro
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? 'Salvataggio...' : 'Conferma Attività'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
